import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';

import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';
import Page from 'components/Page';
import Link from 'components/Link';
import Money from 'components/Money';
import Loading from 'components/Loading';
import Warning from 'components/Warning';
import VisibleIf from 'components/VisibleIf';
import Collapsible from 'components/Collapsible';
import {ConfirmationModal, OneOffChargeModal} from 'components/CommonModals';
import MemberCheckOutModal from 'components/MemberCheckOutModal';
import DetailsContent from 'components/DetailsContentWithRightSideBar';
import BookingFormInfoSection from 'components/BookingFormInfoSection';
import AgreementUserTakePhotoModal from 'components/AgreementUserTakePhotoModal';

import {
  InvoiceDetailModal,
  DraftInvoiceDetailModal,
  CreditNoteDetailModal,
  PaymentDetailModal,
  OverpaymentDetailModal,
  RefundDetailModal,
  InvoiceCreateModal,
  DraftInvoiceCreateModal,
  CreditNoteCreateModal,
  OverpaymentCreateModal,
  GocardlessCustomerDetailModal,
  GocardlessPaymentDetailModal,
  InitiateGoCardlessPaymentModal,
  CancelGocardlessCustomerModal,
  ConfirmPaymentModal
} from 'components/FinanceModals';

import {
  AddAgreementModal,
  AgreementsTable,
  CreateOfferModal,
  OffersTable,
  TenantInformation,
  SendAgreementModal,
  KycTable,
  AffordabilityTable,
  RentScheduleTable,
  LegacyDocumentsTable,
  LegacyDocumentUploadModal,
  AffordabilityDetailModal,
  AffordabilityDocumentUploadModal,
  KycModal,
  KycDocumentUploadModal,
  KycVerifyVisaModal,
  CreateRentScheduleModal,
  AddGuarantorModal,
  AddEmploymentModal,
  CancelBookingModal,
  AgreementDetailModal,
  SendAgreementReminderModal,
  AgreementUserInfoModal,
  RequestKycDocumentModal,
  AddSecondaryTenantModal,
  RemoveSecondaryTenantModal,
  LegacyDocumentEditModal,
  AddressHistoryModal,
  TenantsBox,
  RoomScheduleTable,
  GocardlessPaymentMethod,
  Schedule,
  RentScheduleExtraOptions,
  OneOffChargesScheduleExtraOptions,
  DepositScheduleExtraOptions,
  EarlyTerminationsTable,
  BreakClauseTable,
  RoomMoveModal,
  BreakClauseNoticeRecordModal,
  TerminateTenancyModal,
  ConfirmTerminationModal,
  RemoveGocardlessInvitationModal,
  RemoveAndVoidGocardlessInvitationModal,
  SetupGocardlessModal,
  SendGoCardlessInvitationConfirmModal,
  BookingDetailRightSideBar,
  ConfirmChargeModal
} from './components';

import {ContainerWithBooking} from 'containers/types';

import {
  MODALS as BOOKING_MODALS,
  fetchKycs,
  fetchBooking,
  fetchAgreements,
  fetchRentSchedule,
  fetchLegacyDocuments,
  fetchAffordabilities,
  fetchKycDetails,
  selectSelectedKycId,
  selectBooking,
  selectOffers,
  selectBookingNotFound,
  resetBookingDetailState,
  passAffordability,
  selectAgreements,
  selectKycs,
  selectAffordabilities,
  selectLegacyDocuments,
  selectRentSchedule,
  cancelAgreement,
  signAgreementManually,
  fetchSendAgreementReminderDetails,
  removeSecondaryTenant,
  removeGuarantor,
  fetchKycSectionDetails,
  KYC_SECTION_TYPES,
  REFETCH_AGREEMENT_DELAY,
  fetchAgreement,
  deleteLegacyDocument,
  checkOutTenantsEarly,
  undoCheckOutTenantsEarly
} from 'ducks/bookings';
import {
  openModal,
  isModalOpen,
  MODALS as BASE_MODALS,
  storeDataInModal,
  closeModal,
  openInformationModal
} from 'ducks/modals';
import {
  requiresPermission,
  PERMISSIONS,
  requiresMultiplePermissions,
  selectCurrentUserPermissions
} from 'ducks/auth';
import {MODALS as AGREEMENT_USERS_MODALS} from 'ducks/agreementUsers';
import {delayedAction} from 'ducks/common';
import {undoCheckInTenants} from 'ducks/frontDesk';
import {
  SCHEDULE_TYPES,
  MODALS as TENANCY_MODALS,
  SCHEDULE_ITEM_TYPES,
  fetchTenancy,
  fetchRentSchedule as fetchTenancyRentSchedule,
  fetchDepositSchedule,
  fetchOneOffSchedule,
  fetchRoomAllocations,
  fetchXeroOrganisation,
  fetchEarlyTerminations,
  selectCurrentBalance,
  selectRoomAllocations,
  selectEarlyTerminations,
  selectTenancy,
  deleteEarlyTermination,
  fetchGocardlessCustomerAndInvitation,
  selectGocardlessCustomerAndInvitation,
  removeGocardlessCustomer,
  fetchSchedule,
  FETCH_SCHEDULE_DELAY
} from 'ducks/tenancy';

import {fetchBookingNotes, selectBookingNotes} from 'ducks/notes';

import {openUrlInNewTab} from 'utils/files';
import {openModalFromUrl} from 'utils/modals';
import {hasEntityPermission} from 'utils/permissions';
import {reverse} from 'utils/urls';
import {BOOKING_SET_DETAIL_URL} from 'config/urls';

import './styles.css';

const VALIDATION_TAB = 'Validation';
const STAY_MANAGEMENT_TAB = 'Stay Management';
const FINANCE_TAB = 'Finance';

const TABS = [VALIDATION_TAB, STAY_MANAGEMENT_TAB, FINANCE_TAB];

const TabNavigation = ({selectedTab, selectTab}) => (
  <div className="TabNavigation colab-centered-v">
    {TABS.map(tab => (
      <div
        className={classNames(
          'TabNavigation__tab colab-centered-v colab-clickable',
          {
            'TabNavigation__tab--active': selectedTab == tab
          }
        )}
        key={tab}
        onClick={() => selectTab(tab)}>
        {tab}
      </div>
    ))}
  </div>
);

class BookingDetail extends React.Component<
  ContainerWithBooking &
    LoginRequiredProps &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  state = {
    selectedTab: VALIDATION_TAB
  };

  componentDidMount() {
    const {locationRef, bookingId} = this.props.match.params;

    const pageModals = {
      ...BOOKING_MODALS,
      ...AGREEMENT_USERS_MODALS,
      ...TENANCY_MODALS
    };

    openModalFromUrl(pageModals, this.props.openModal);
    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        this.props.fetchBooking({locationRef, bookingId});

        this.props.fetchAgreements({bookingId});

        if (
          hasEntityPermission({
            locationRef,
            permission: PERMISSIONS.VIEW_KYC_OVERVIEW,
            userPermissions: this.props.userPermissions
          })
        ) {
          this.props.fetchKycs({bookingId});
        }

        this.props.fetchAffordabilities({bookingId});

        if (
          hasEntityPermission({
            locationRef,
            permission: PERMISSIONS.VIEW_RENT_SCHEDULE,
            userPermissions: this.props.userPermissions
          })
        ) {
          this.props.fetchRentSchedule({bookingId});
        }

        if (
          hasEntityPermission({
            locationRef,
            permission: PERMISSIONS.VIEW_LEGACY_DOCUMENTS,
            userPermissions: this.props.userPermissions
          })
        ) {
          this.props.fetchLegacyDocuments({bookingId});
        }

        if (
          hasEntityPermission({
            locationRef,
            permission: PERMISSIONS.VIEW_BOOKING_NOTES,
            userPermissions: this.props.userPermissions
          })
        ) {
          this.props.fetchBookingNotes({bookingId, archived: false});
        }
      }
    );
  }

  componentDidUpdate(prevProps) {
    const {locationRef} = this.props.match.params;
    const tenancyId = _.get(this.props, 'booking.tenancy.id');

    if (_.get(this.props, 'booking.id') !== _.get(prevProps, 'booking.id')) {
      this.props.fetchTenancy({tenancyId});
      this.props.fetchXeroOrganisation({locationRef});
      this.props.fetchTenancyRentSchedule({tenancyId});
      this.props.fetchDepositSchedule({tenancyId});
      this.props.fetchOneOffSchedule({tenancyId});
      this.props.fetchRoomAllocations({tenancyId});
      this.props.fetchEarlyTerminations({tenancyId});
      this.props.fetchGocardlessCustomerAndInvitation({tenancyId});
    }
  }

  componentWillUnmount() {
    this.props.resetBookingDetailState();
  }

  openConfirmationModal = ({heading, confirmAction, question, ...args}) =>
    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading,
      question,
      confirmAction,
      ...args
    });

  openCancelBookingModal = () => {
    const {locationRef, bookingId} = this.props.match.params;

    this.props.requiresPermission(
      {locationRef, permission: PERMISSIONS.CANCEL_BOOKING},
      () =>
        this.props.openModal(BOOKING_MODALS.CANCEL_BOOKING, {
          bookingId,
          locationRef
        })
    );
  };

  openMemberCheckOutModal = () => {
    const {match: {params: {locationRef, bookingId}}} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_CHECK_OUT,
        locationRef
      },
      () => {
        this.props.openModal(
          BASE_MODALS.MEMBER_CHECK_OUT,
          {
            locationRef,
            bookingId,
            confirmAction: extraActions =>
              this.props.checkOutTenantsEarly({bookingId}, extraActions),
            warning:
              'You are about to do an early check out. This will update stay dates and room allocations.'
          },
          true
        );
      }
    );
  };

  undoCheckOutTenantsEarly = () => {
    const {bookingId, locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_CHECK_OUT,
        locationRef
      },
      () => {
        this.props.openModal(BASE_MODALS.CONFIRM, {
          heading: 'Undo early checkout',
          question: 'Are you sure you want to undo this early checkout?',
          confirmAction: () =>
            this.props.undoCheckOutTenantsEarly({bookingId}, [
              () => closeModal(BASE_MODALS.CONFIRM),
              () => fetchBooking({bookingId, locationRef})
            ])
        });
      }
    );
  };

  openOneOffChargeModal = () => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_CHARGE,
        locationRef
      },
      () => this.props.openModal(BASE_MODALS.ONE_OFF_CHARGE)
    );
  };

  openCreateOfferModal = () => {
    const {locationRef, bookingId} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.CREATE_NEW_OFFER,
        locationRef
      },
      () => {
        this.props.openModal(BOOKING_MODALS.CREATE_OFFER, {
          bookingId,
          locationRef
        });
      }
    );
  };

  openAgreementUserInfoModal = agreementUserId => {
    const {locationRef, bookingId} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.VIEW_MEMBER_INFORMATION
      },
      () => {
        const modalName = AGREEMENT_USERS_MODALS.AGREEMENT_USER_INFO;

        this.props.openModal(
          modalName,
          {agreementUserId, locationRef, bookingId},
          true
        );
      }
    );
  };

  openAddGuarantorModal = () => {
    const {locationRef, bookingId} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.ADD_GUARANTOR,
        locationRef: locationRef
      },
      () =>
        this.props.openModal(BOOKING_MODALS.ADD_GUARANTOR, {
          locationRef,
          bookingId
        })
    );
  };

  openAddAgreementModal = () => {
    const {locationRef, bookingId} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.ADD_AGREEMENT,
        locationRef
      },
      () => {
        this.props.openModal(BOOKING_MODALS.ADD_AGREEMENT, {bookingId});
      }
    );
  };

  openAddSecondaryTenantModal = () => {
    const {locationRef, bookingId} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.ADD_SECONDARY_TENANT,
        locationRef: locationRef
      },
      () =>
        this.props.openModal(BOOKING_MODALS.ADD_SECONDARY_TENANT, {
          locationRef,
          bookingId
        })
    );
  };

  openRemoveSecondaryTenantModal = () => {
    const {locationRef} = this.props.match.params;

    this.props.requiresMultiplePermissions(
      {
        permissions: [
          {
            permission: PERMISSIONS.REMOVE_SECONDARY_TENANT,
            locationRef
          },
          {permission: PERMISSIONS.SETUP_GOCARDLESS}
        ],
        disableClose: false
      },
      () => this.props.openModal(BOOKING_MODALS.REMOVE_SECONDARY_TENANT)
    );
  };

  removeGuarantor = () => {
    const {booking} = this.props;
    const successExtraActions = [
      () => this.props.closeModal(BASE_MODALS.CONFIRM),
      ...this.refetchExtraActions()
    ];

    const failureExtraActions = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {
            errors,
            submitting: false
          }
        })
    ];

    this.props.removeGuarantor(
      {bookingId: booking.id},
      successExtraActions,
      failureExtraActions
    );
  };

  openRemoveGuarantorModal = () => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.REMOVE_GUARANTOR,
        locationRef
      },
      () =>
        this.openConfirmationModal({
          heading: 'Remove Guarantor',
          question: 'Are you sure you want to remove the guarantor?',
          confirmAction: this.removeGuarantor
        })
    );
  };

  openSendAgreementModal = agreementId => {
    const {bookingId, locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {locationRef, permission: PERMISSIONS.SEND_AGREEMENT},
      () => {
        this.props.openModal(BOOKING_MODALS.SEND_AGREEMENT, {
          bookingId,
          agreementId
        });
      }
    );
  };

  openSignAgreementManuallyModal = agreementId => {
    const {bookingId, locationRef} = this.props.match.params;
    const tenancyId = _.get(this.props, 'booking.tenancy.id');

    const successExtraActions = [
      () => this.props.closeModal(BASE_MODALS.CONFIRM),
      () =>
        fetchAgreement({agreementId}, [
          data =>
            storeDataInModal({
              modalName: BOOKING_MODALS.AGREEMENT_DETAIL,
              data
            })
        ]),
      () => fetchAgreements({bookingId, locationRef}),
      () => fetchBooking({bookingId, locationRef}),
      () => fetchTenancyRentSchedule({tenancyId})
    ];

    this.props.requiresPermission(
      {locationRef, permission: PERMISSIONS.SIGN_AGREEMENT_MANUALLY},
      () => {
        this.openConfirmationModal({
          heading: 'Sign agreement manually',
          question: 'Are you sure you want to manually sign this agreement?',
          confirmAction: () =>
            this.props.signAgreementManually(
              {agreement: agreementId},
              successExtraActions
            )
        });
      }
    );
  };

  cancelAgreementConfirmationAction = agreement => {
    const delayedFetchAgreement = () =>
      delayedAction({
        delay: REFETCH_AGREEMENT_DELAY,
        action: () =>
          fetchAgreement({agreementId: agreement}, [
            data =>
              storeDataInModal({
                modalName: BOOKING_MODALS.AGREEMENT_DETAIL,
                data
              })
          ])
      });

    const successExtraActions = [
      () => this.props.closeModal(BASE_MODALS.CONFIRM),
      () =>
        fetchSendAgreementReminderDetails({agreement}, [
          data =>
            storeDataInModal({modalName: BOOKING_MODALS.AGREEMENT_DETAIL, data})
        ]),
      delayedFetchAgreement
    ];

    const failureExtraActions = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {errors, submitting: false}
        })
    ];

    this.props.cancelAgreement(
      {agreement},
      successExtraActions,
      failureExtraActions
    );
  };

  openCancelAgreementModal = agreementId =>
    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.CANCEL_AGREEMENT
      },
      () =>
        this.openConfirmationModal({
          heading: 'Cancel Signature Request',
          confirmButtonText: 'Cancel request',
          question: `
          Are you sure you want to cancel this signature request?
          No one will be able to sign it and all copies of the document will be deleted from HelloSign.
        `,
          confirmAction: () =>
            this.cancelAgreementConfirmationAction(agreementId)
        })
    );

  openLegacyDocumentUploadModal = () => {
    const {bookingId, locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.UPLOAD_LEGACY_DOCUMENT,
        locationRef
      },
      () => {
        this.props.openModal(BOOKING_MODALS.LEGACY_DOCUMENT_UPLOAD, {
          bookingId
        });
      }
    );
  };

  openAffordabilityModal = affordabilityId => {
    const {bookingId, locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.VIEW_EMPLOYMENT
      },
      () => {
        this.props.openModal(
          BOOKING_MODALS.AFFORDABILITY_DETAIL,
          {affordabilityId, bookingId},
          true
        );
      }
    );
  };

  openAffordabilityDocumentUploadModal = (agreementUserId, affordabilityId) => {
    const {locationRef, bookingId} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.EDIT_EMPLOYMENT, locationRef},
      () => {
        this.props.openModal(BOOKING_MODALS.AFFORDABILITY_DOCUMENT_UPLOAD, {
          agreementUserId,
          affordabilityId,
          bookingId
        });
      }
    );
  };

  openKycModal = ({kycId}) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.VIEW_KYC_DETAILED_VIEW
      },
      () => {
        const bookingId = this.props.booking.id;

        this.props.openModal(
          BOOKING_MODALS.KYC,
          {kycId, bookingId, locationRef},
          true
        );
      }
    );
  };

  openAddAddressHistoryModal = ({agreementUserId}) => {
    this.props.openModal(BOOKING_MODALS.ADDRESS_HISTORY, {
      agreementUserId,
      locationRef: this.props.match.params.locationRef
    });
  };

  openAssessAffordabilityModal = () => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {locationRef, permission: PERMISSIONS.ASSESS_AFFORDABILITY},
      () =>
        this.openConfirmationModal({
          heading: 'Assess Affordability',
          question:
            'Are you sure you want to pass this affordability for booking?',
          confirmAction: this.assessAffordabilityConfirmAction
        })
    );
  };

  assessAffordabilityConfirmAction = () => {
    const {locationRef, bookingId} = this.props.match.params;

    const successExtraActions = [
      () => this.props.closeModal(BASE_MODALS.CONFIRM),
      () => this.props.fetchBooking({locationRef, bookingId})
    ];

    const failureExtraActions = [
      ({errors}) =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {
            errors,
            submitting: false
          }
        })
    ];

    this.props.passAffordability(
      {locationRef, bookingId},
      successExtraActions,
      failureExtraActions
    );
  };

  openCreateRentScheduleModal = () => {
    const {locationRef, bookingId} = this.props.match.params;
    const tenancyId = _.get(this.props, 'booking.tenancy.id');

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.CREATE_PAYMENT_SCHEDULE
      },
      () => {
        const {booking: {period, pwRent}} = this.props;

        this.props.openModal(BOOKING_MODALS.CREATE_RENT_SCHEDULE, {
          pwRent,
          period,
          bookingId,
          tenancyId,
          locationRef
        });
      }
    );
  };

  downloadAgreement = contractUrl =>
    this.props.requiresPermission(
      {
        permission: PERMISSIONS.DOWNLOAD_AGREEMENT,
        locationRef: this.props.match.params.locationRef
      },
      () => openUrlInNewTab(contractUrl)
    );

  openAgreementDetailModal = agreementId => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_AGREEMENT,
        locationRef
      },
      () =>
        this.props.openModal(
          BOOKING_MODALS.AGREEMENT_DETAIL,
          {agreementId, locationRef},
          true
        )
    );
  };

  deleteLegacyDocument = ({legacyDocument}) => {
    const {bookingId} = this.props.match.params;
    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      () => fetchLegacyDocuments({bookingId})
    ];
    const failureExtraActions = [
      errors =>
        storeDataInModal({
          modalName: BASE_MODALS.CONFIRM,
          data: {errors, submitting: false}
        })
    ];

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_DELETE_LEGACY_DOCUMENT,
        locationRef: this.props.match.params.locationRef
      },
      () =>
        this.openConfirmationModal({
          heading: 'Delete Legacy Document',
          question: `Are you sure you want to delete legacy document ${
            legacyDocument.document.filename
          }?`,
          confirmAction: () =>
            this.props.deleteLegacyDocument(
              {legacyDocumentId: legacyDocument.id},
              successExtraActions,
              failureExtraActions
            )
        })
    );
  };

  openLegacyDocumentEditModal = ({legacyDocument}) => {
    this.props.requiresPermission(
      {
        permission: PERMISSIONS.EDIT_DELETE_LEGACY_DOCUMENT,
        locationRef: this.props.match.params.locationRef
      },
      () =>
        this.props.openModal(BOOKING_MODALS.LEGACY_DOCUMENT_EDIT, {
          legacyDocument
        })
    );
  };

  fetchKycDetails = kycId => {
    const {match: {params: {bookingId}}} = this.props;

    const sectionExtraActions = [
      section =>
        storeDataInModal({
          modalName: BOOKING_MODALS.KYC,
          data: {[section.type]: section}
        })
    ];

    const extraActions = [
      ({sections, id}) =>
        fetchKycSectionDetails(
          {
            sectionId: sections[KYC_SECTION_TYPES.POID],
            bookingId,
            kycId: id
          },
          sectionExtraActions
        ),
      ({sections, id}) =>
        fetchKycSectionDetails(
          {
            sectionId: sections[KYC_SECTION_TYPES.POA],
            bookingId,
            kycId: id
          },
          sectionExtraActions
        ),
      ({sections, id}) =>
        fetchKycSectionDetails(
          {
            sectionId: sections[KYC_SECTION_TYPES.VISA],
            bookingId,
            kycId: id
          },
          sectionExtraActions
        ),
      kyc =>
        storeDataInModal({
          modalName: BOOKING_MODALS.KYC,
          data: {
            kyc,
            verifications: [],
            documentRequests: []
          }
        })
    ];

    return this.props.fetchKycDetails({kycId, bookingId}, extraActions);
  };

  openSendAgreementReminderModal = agreementId =>
    this.props.requiresPermission(
      {
        permission: PERMISSIONS.SEND_AGREEMENT_REMINDER,
        locationRef: this.props.match.params.locationRef
      },
      () =>
        this.props.openModal(BOOKING_MODALS.SEND_AGREEMENT_REMINDER, {
          agreementId
        })
    );

  openAddEmploymentModal = (agreementUserId, affordabilityId) => {
    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.EDIT_EMPLOYMENT
      },
      () => {
        const {locationRef, bookingId} = this.props.match.params;

        this.props.openModal(BOOKING_MODALS.ADD_EMPLOYMENT, {
          agreementUserId,
          affordabilityId,
          locationRef,
          bookingId
        });
      }
    );
  };

  openKycDocumentUploadModal = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.EDIT_KYC
      },
      () => {
        this.props.openModal(BOOKING_MODALS.KYC_DOCUMENT_UPLOAD);
      }
    );

  openVerifyVisaModal = () =>
    this.props.openModal(BOOKING_MODALS.KYC_VERIFY_VISA);

  openRequestKycDocumentModal = ({
    bookingId,
    kycSectionsIds,
    selectedSectionType
  }) =>
    this.props.openModal(BOOKING_MODALS.REQUEST_KYC_DOCUMENT, {
      bookingId,
      kycSectionsIds,
      selectedSectionType
    });

  refetchExtraActions = () => {
    // TODO: Pass this to modals
    const {locationRef, bookingId} = this.props.match.params;
    return [
      () => this.props.fetchBooking({locationRef, bookingId}),
      () => this.props.fetchAgreements({bookingId}),
      () => this.props.fetchKycs({bookingId}),
      () => this.props.fetchAffordabilities({bookingId})
    ];
  };

  handleRowClick = (item, itemId, scheduleType) =>
    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS
      },
      () => {
        switch (item) {
          case SCHEDULE_ITEM_TYPES.INVOICE:
            this.openInvoiceDetailModal(scheduleType, itemId);
            break;
          case SCHEDULE_ITEM_TYPES.CREDIT_NOTE:
            this.openCreditNoteDetailModal(scheduleType, itemId);
            break;
          case SCHEDULE_ITEM_TYPES.PAYMENT:
            this.openPaymentDetailModal(scheduleType, itemId);
            break;
          case SCHEDULE_ITEM_TYPES.OVERPAYMENT:
            this.openOverpaymentDetailModal(scheduleType, itemId);
            break;
          case SCHEDULE_ITEM_TYPES.REFUND:
            this.openRefundDetailModal(scheduleType, itemId);
            break;
          case SCHEDULE_ITEM_TYPES.DRAFT_INVOICE:
            this.openDraftInvoiceDetailModal(scheduleType, itemId);
            break;
          default:
            return;
        }
      }
    );

  openCreateCreditNoteModal = (scheduleType, invoice) =>
    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.CREATE_CREDIT_NOTE
      },
      () => {
        const {match: {params: {locationRef}}, primaryTenant} = this.props;
        const tenancyId = _.get(this.props.booking, 'tenancy.id');

        this.props.openModal(TENANCY_MODALS.CREDIT_NOTE_CREATE, {
          scheduleType,
          invoice,
          locationRef,
          tenancyId,
          primaryTenant: {fullName: _.get(primaryTenant, 'fullName')}
        });
      }
    );

  openCreateInvoiceModal = scheduleType => {
    const tenancyId = _.get(this.props.booking, 'tenancy.id');
    const {bookingId} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.CREATE_INVOICE
      },
      () => {
        const {match: {params: {locationRef}}, primaryTenant} = this.props;

        this.props.openModal(TENANCY_MODALS.INVOICE_CREATE, {
          scheduleType,
          locationRef,
          tenancyId,
          bookingId,
          primaryTenant
        });
      }
    );
  };

  openCreateDraftInvoiceModal = (scheduleType, scheduleId) => {
    // TODO: Drop tenancyId usage!
    const tenancyId = _.get(this.props.booking, 'tenancy.id');
    const {bookingId} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.CREATE_INVOICE
      },
      () => {
        const {match: {params: {locationRef}}, primaryTenant} = this.props;

        this.props.openModal(BOOKING_MODALS.CREATE_DRAFT_INVOICE, {
          scheduleType,
          scheduleId,
          locationRef,
          tenancyId,
          bookingId,
          primaryTenant
        });
      }
    );
  };

  openCreateOverpaymentModal = scheduleType => {
    const {primaryTenant} = this.props;

    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.CREATE_OVERPAYMENT
      },
      () => {
        const {locationRef} = this.props.match.params;
        const tenancyId = _.get(this.props, 'booking.tenancy.id');
        this.props.openModal(TENANCY_MODALS.OVERPAYMENT_CREATE, {
          scheduleType,
          locationRef,
          tenancyId,
          primaryTenant
        });
      }
    );
  };

  openPaymentDetailModal = (scheduleType, paymentId) => {
    const {match: {params: {locationRef}}} = this.props;
    const tenancyId = _.get(this.props, 'booking.tenancy.id');

    this.props.openModal(
      TENANCY_MODALS.PAYMENT_DETAIL,
      {
        scheduleType,
        paymentId,
        locationRef,
        tenancyId
      },
      true
    );
  };

  openRefundDetailModal = (scheduleType, refundId) => {
    const {match: {params: {locationRef}}} = this.props;
    const tenancyId = _.get(this.props, 'booking.tenancy.id');

    this.props.openModal(
      TENANCY_MODALS.REFUND_DETAIL,
      {
        scheduleType,
        refundId,
        locationRef,
        tenancyId
      },
      true
    );
  };

  openInvoiceDetailModal = (scheduleType, invoiceId) => {
    const {match: {params: {locationRef}}} = this.props;
    const tenancyId = _.get(this.props, 'booking.tenancy.id');

    this.props.openModal(
      TENANCY_MODALS.INVOICE_DETAIL,
      {
        scheduleType,
        invoiceId,
        locationRef,
        tenancyId
      },
      true
    );
  };

  openDraftInvoiceDetailModal = (scheduleType, draftInvoiceId) => {
    const {match: {params: {locationRef}}} = this.props;
    const tenancyId = _.get(this.props, 'booking.tenancy.id');

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS, locationRef},
      () => {
        this.props.openModal(
          BOOKING_MODALS.DRAFT_INVOICE_DETAIL,
          {
            scheduleType,
            draftInvoiceId,
            locationRef,
            tenancyId
          },
          true
        );
      }
    );
  };

  openGocardlessCustomerModal = gocardlessCustomerId => {
    const {match: {params: {locationRef}}} = this.props;
    const tenancyId = _.get(this.props, 'booking.tenancy.id');

    this.props.openModal(
      TENANCY_MODALS.GOCARDLESS_CUSTOMER_DETAIL,
      {
        gocardlessCustomerId,
        locationRef,
        tenancyId
      },
      true
    );
  };

  openGocardlessPaymentDetailModal = (scheduleType, gocardlessPaymentId) => {
    const {match: {params: {locationRef}}} = this.props;
    const tenancyId = _.get(this.props, 'booking.tenancy.id');

    this.props.openModal(
      TENANCY_MODALS.GOCARDLESS_PAYMENT_DETAIL,
      {
        gocardlessPaymentId,
        scheduleType,
        tenancyId,
        locationRef
      },
      true
    );
  };

  openCreditNoteDetailModal = (scheduleType, creditNoteId) => {
    const {locationRef} = this.props.match.params;
    const tenancyId = _.get(this.props, 'booking.tenancy.id');

    this.props.openModal(
      TENANCY_MODALS.CREDIT_NOTE_DETAIL,
      {
        scheduleType,
        creditNoteId,
        locationRef,
        tenancyId
      },
      true
    );
  };

  openOverpaymentDetailModal = (scheduleType, overpaymentId) => {
    const {locationRef} = this.props.match.params;
    const tenancyId = _.get(this.props, 'booking.tenancy.id');

    this.props.openModal(
      TENANCY_MODALS.OVERPAYMENT_DETAIL,
      {
        scheduleType,
        overpaymentId,
        locationRef,
        tenancyId
      },
      true
    );
  };

  openTerminateTenancyModal = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.CREATE_EARLY_TERMINATION
      },
      () => this.props.openModal(TENANCY_MODALS.TERMINATE_TENANCY)
    );

  openBreakClauseNoticeRecordModal = () => {
    this.props.openModal(TENANCY_MODALS.BREAK_CLAUSE_NOTICE_RECORD);
  };

  openInitiateGoCardlessPaymentModal = ({
    invoiceId,
    tenancyId,
    amount,
    description,
    scheduleType,
    isDueDateInFuture,
    invoice
  }) => {
    this.props.openModal(TENANCY_MODALS.INITIATE_GOCARDLESS_PAYMENT, {
      invoiceId,
      tenancyId,
      amount,
      description,
      scheduleType,
      isDueDateInFuture,
      invoice
    });
  };

  openRoomMoveModal = () => {
    const {checkedInAt, bookingType} = this.props.booking;

    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.CREATE_ROOM_MOVE
      },
      () =>
        this.props.openModal(TENANCY_MODALS.ROOM_MOVE, {
          checkedInAt,
          bookingType
        })
    );
  };

  openDeleteEarlyTerminationModal = ({terminationId}) => {
    this.openConfirmationModal({
      heading: 'Delete Early Termination',
      question:
        'Are you sure you want to delete the early termination  for this tenancy?',
      confirmAction: () => this.deleteEarlyTermination({terminationId})
    });
  };

  deleteEarlyTermination = ({terminationId}) => {
    const tenancyId = _.get(this.props.booking, 'tenancy.id');
    const {bookingId, locationRef} = this.props.match.params;

    const successExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      () => fetchBooking({bookingId, locationRef}),
      () => fetchEarlyTerminations({tenancyId}),
      () => fetchRoomAllocations({tenancyId})
    ];

    const failureExtraActions = [
      () => closeModal(BASE_MODALS.CONFIRM),
      ({errors}) =>
        openInformationModal({
          message: _.get(errors, '_error'),
          heading: 'Edit/ Adjust early termination'
        })
    ];

    this.props.deleteEarlyTermination(
      {terminationId, tenancyId},
      successExtraActions,
      failureExtraActions
    );
  };

  openRemoveGocardlessInvitationModal = (invitationId: number) => {
    this.props.requiresPermission(
      {permission: PERMISSIONS.SETUP_GOCARDLESS},
      () => {
        this.props.openModal(TENANCY_MODALS.REMOVE_GOCARDLESS_INVITATION, {
          invitationId
        });
      }
    );
  };

  openRemoveAndVoidGocardlessInvitationModal = (invitationId: number) => {
    this.props.openModal(TENANCY_MODALS.REMOVE_AND_VOID_GOCARDLESS_INVITATION, {
      invitationId
    });
  };

  removeGocardlessCustomer = (customerId: number) => {
    const tenancyId = _.get(this.props.booking, 'tenancy.id');

    this.props.requiresPermission(
      {permission: PERMISSIONS.SETUP_GOCARDLESS},
      () => {
        const successExtraActions: any = [
          () => closeModal(BASE_MODALS.CONFIRM),
          () => fetchGocardlessCustomerAndInvitation({tenancyId})
        ];
        const failureExtraActions: any = [
          errors =>
            storeDataInModal({
              modalName: BASE_MODALS.CONFIRM,
              data: {errors, submitting: false}
            })
        ];

        this.openConfirmationModal({
          heading: 'Remove GoCarldess Mandate',
          question:
            'Are you sure you want to remove this GoCardless mandate from the booking?',
          confirmAction: () =>
            this.props.removeGocardlessCustomer(
              {tenancyId, customerId},
              successExtraActions,
              failureExtraActions
            )
        });
      }
    );
  };

  openGoCardlessItemUrlLink = (event, url) => {
    event.preventDefault();

    this.props.requiresPermission(
      {permission: PERMISSIONS.SETUP_GOCARDLESS},
      () => {
        if (!_.isNil(url)) {
          window.open(url, '_blank');
        }
      }
    );
  };

  openSetupGocardlessModal = () => {
    const tenancyId = _.get(this.props.booking, 'tenancy.id');
    const {match: {params: {locationRef}}} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.SETUP_GOCARDLESS},
      () => {
        const bookingId = this.props.tenancy.bookingId;

        this.props.openModal(TENANCY_MODALS.SETUP_GOCARDLESS, {
          bookingId,
          tenancyId,
          locationRef
        });
      }
    );
  };

  openSendGoCardlessInvitationWarningsModal = tenantId => {
    const tenancyId = _.get(this.props.booking, 'tenancy.id');

    this.props.openModal(TENANCY_MODALS.SEND_GOCARDLESS_INVITATION_CONFIRM, {
      tenancyId,
      tenantId
    });
  };

  selectTab = selectedTab => {
    this.setState({selectedTab});
  };

  financeRefetchExtraActions = ({tenancyId, scheduleType}) => [
    () => fetchSchedule({tenancyId, scheduleType})
  ];

  financeDelayedExtraActions = ({tenancyId, scheduleType}) => [
    () => fetchSchedule({tenancyId, scheduleType})
  ];

  openConfirmPaymentModal = invoice => {
    const booking = {
      period: this.props.booking.period,
      status: this.props.booking.status,
      room: this.props.booking.room,
      primaryTenant: this.props.primaryTenant
    };

    const invoiceDescription = _.get(invoice, 'items[0].description');

    this.props.openModal(TENANCY_MODALS.CONFIRM_PAYMENT, {
      invoiceDescription,
      booking
    });
  };

  memberCheckOutExtraActions = () => {
    const {bookingId, locationRef} = this.props.match.params;

    return [() => fetchBooking({bookingId, locationRef})];
  };

  openConfirmChargeModal = () => {
    this.props.openModal(BOOKING_MODALS.CONFIRM_CHARGE);
  };

  undoCheckInTenants = () => {
    const {bookingId, locationRef} = this.props.match.params;

    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading: 'Undo check in member',
      question: 'Are you sure you want to undo this check in?',
      confirmAction: () =>
        this.props.undoCheckInTenants({bookingId}, [
          () => closeModal(BASE_MODALS.CONFIRM),
          () => fetchBooking({bookingId, locationRef})
        ])
    });
  };

  render() {
    const {
      booking,
      match: {params: {locationRef, bookingId}},
      modals: {
        cancelBookingModalOpened,
        createOfferModal,
        addAgreementModal,
        sendAgreementModal,
        legacyDocumentUploadModal,
        affordabilityDetailModal,
        affordabilityDocumentUploadModal,
        kycModal,
        kycDocumentUploadModal,
        kycVerifyVisaModal,
        addressHistoryModal,
        createRentScheduleModal,
        addGuarantorModal,
        addSecondaryTenantModal,
        agreementUserInfoModal,
        addEmploymentModal,
        confirmationModal,
        agreementDetailModal,
        sendAgreementReminderModal,
        requestKycDocumentModal,
        removeSecondaryTenantModal,
        legacyDocumentEditModal,
        agreementUserTakePhotoModal,
        invoiceDetailModal,
        draftInvoiceDetailModal,
        invoiceCreateModal,
        draftInvoiceCreateModal,
        creditNoteDetailModal,
        creditNoteCreateModal,
        paymentDetailModal,
        overpaymentCreateModal,
        overpaymentDetailModal,
        refundDetailModal,
        gocardlessCustomerDetailModal,
        breakClauseNoticeModal,
        terminateTenancyModal,
        gocardlessPaymentDetailModal,
        confirmTerminationModal,
        roomMoveModal,
        initiateGoCardlessConfirmModal,
        removeGocardlessInvitationModal,
        removeAndVoidGocardlessInvitationModal,
        setupGocardlessModal,
        sendGocardlessInvitationConfirmModal,
        cancelGocardlessCustomerModal,
        oneOffChargeModal,
        confirmChargeModal,
        confirmPaymentModal,
        memberCheckOutModal
      },
      offers,
      bookingNotFound,
      kycs,
      affordabilities,
      rentSchedule,
      agreements,
      legacyDocuments,
      selectedKycId,
      tenancy,
      roomAllocations,
      rentCurrentBalance,
      oneOffScheduleCurrentBalance,
      depositCurrentBalance,
      schedules,
      gocardlessCustomerAndInvitation: {
        currentGocardlessCustomer,
        currentGocardlessInvitation
      },
      earlyTerminations,
      bookingNotes,
      primaryTenant,
      secondaryTenant
    } = this.props;

    const breakClause = _.get(tenancy, 'breakClause');
    const scheduleExtraHeader = amount => (
      <div className="currentBalance colab-centered-h">
        <h4>Current Balance</h4>
        <Money semibold amount={amount} />
      </div>
    );

    const scheduleBalancesHeader = ({currentBalance, totalBalance}) => (
      <div>
        <div className="currentBalance colab-centered-h">
          <h4>Current Balance</h4>
          <Money semibold amount={currentBalance} />
        </div>
        <div className="currentBalance colab-centered-h">
          <h4>Total Balance</h4>
          <Money semibold amount={totalBalance} />
        </div>
      </div>
    );

    const tenancyId = _.get(this.props, 'booking.tenancy.id');
    const bookingSetId = _.get(booking, 'bookingSetId');
    const bookingFormInfo = _.get(booking, 'bookingFormInfo');

    const extraActions = [
      () => closeModal(BOOKING_MODALS.CONFIRM_CHARGE),
      () => closeModal(BASE_MODALS.ONE_OFF_CHARGE),
      () =>
        delayedAction({
          delay: FETCH_SCHEDULE_DELAY,
          action: () => fetchOneOffSchedule({tenancyId})
        })
    ];

    return (
      <Page activeTab="bookings" locationRef={locationRef}>
        <VisibleIf
          permission={PERMISSIONS.VIEW_BOOKING}
          locationRef={locationRef}>
          {bookingNotFound && <Warning>Booking not found.</Warning>}
          {!booking && !bookingNotFound && <Loading />}
          {booking && (
            <DetailsContent className="BookingDetail">
              <DetailsContent.Header>
                <span className="BookingDetail__content__navigation">
                  <Link
                    to={reverse(BOOKING_SET_DETAIL_URL, {
                      locationRef,
                      bookingSetId
                    })}>
                    Booking set #{bookingSetId}
                  </Link>{' '}
                  / Booking #{bookingId}
                </span>
              </DetailsContent.Header>
              <DetailsContent.LeftSideBar>
                <TenantsBox
                  primaryTenant={primaryTenant}
                  secondaryTenant={secondaryTenant}
                  guarantor={booking.guarantor}
                  bookingType={booking.bookingType}
                  bookingStatus={booking.status}
                  room={booking.room}
                  startDate={booking.period.startDate}
                  endDate={booking.period.endDate}
                  stayDuration={booking.period.difference}
                  rentPerWeek={booking.pwRent}
                  totalValue={booking.rentScheduleTotalInvoicesAmount}
                  cancelBooking={this.openCancelBookingModal}
                  undoCheckInTenants={this.undoCheckInTenants}
                  bookingOrganisation={booking.bookingOrganisation}
                  verificationMethod={booking.verificationMethod}
                  bookingCanBeCancelled={booking.bookingCanBeCancelled}
                  checkOutBookingEarly={this.openMemberCheckOutModal}
                  bookingCanBeCheckedOutEarly={
                    booking.bookingCanBeEarlyCheckedOut
                  }
                  undoBookingEarlyCheckout={this.undoCheckOutTenantsEarly}
                  bookingEarlyCheckoutCanBeUndone={
                    booking.earlyCheckOutCanBeUndone
                  }
                  isEarlyCheckedOut={booking.isEarlyCheckedOut}
                  checkedInAt={booking.checkedInAt}
                  checkedOutAt={booking.checkedOutAt}
                  locationRef={locationRef}
                />
              </DetailsContent.LeftSideBar>
              <DetailsContent.Content>
                <TabNavigation
                  selectedTab={this.state.selectedTab}
                  selectTab={this.selectTab}
                />
                {this.state.selectedTab == VALIDATION_TAB && (
                  <React.Fragment>
                    {bookingFormInfo && (
                      <Collapsible name="Booking Form Info">
                        <BookingFormInfoSection
                          initialRoomTypeName={
                            bookingFormInfo.initialRoomTypeName
                          }
                          isAffordable={bookingFormInfo.isAffordable}
                          isStudent={bookingFormInfo.isStudent}
                          stripePaymentId={bookingFormInfo.stripePaymentId}
                          initialBillingFrequency={
                            bookingFormInfo.initialBillingFrequency
                          }
                          salesAgent={bookingFormInfo.salesAgent}
                          referralCode={bookingFormInfo.referralCode}
                          initialRoomPrice={bookingFormInfo.initialRoomPrice}
                          originalLengthOfStay={
                            bookingFormInfo.initialLengthOfStay
                          }
                          floorPreference={bookingFormInfo.floorPreference}
                          initialBillingWithGuarantor={
                            bookingFormInfo.initialBillingWithGuarantor
                          }
                          securityDepositWeeks={
                            bookingFormInfo.securityDepositWeeks
                          }
                        />
                      </Collapsible>
                    )}

                    <Collapsible name="Offers">
                      <VisibleIf
                        permission={PERMISSIONS.VIEW_OFFERS}
                        locationRef={locationRef}>
                        <OffersTable
                          offers={offers}
                          source={booking.source}
                          openCreateOfferModal={this.openCreateOfferModal}
                        />
                      </VisibleIf>
                    </Collapsible>

                    <Collapsible
                      name="Member Information"
                      className="TenantInformationContainer">
                      <VisibleIf
                        permission={PERMISSIONS.VIEW_MEMBER_INFORMATION}
                        locationRef={locationRef}>
                        <TenantInformation
                          locationRef={locationRef}
                          tenants={booking.tenants}
                          guarantor={booking.guarantor}
                          roomTypeCapacity={_.get(
                            booking,
                            'room.roomType.capacity',
                            0
                          )}
                          openAgreementUserInfoModal={
                            this.openAgreementUserInfoModal
                          }
                          openAddGuarantorModal={this.openAddGuarantorModal}
                          openAddSecondaryTenantModal={
                            this.openAddSecondaryTenantModal
                          }
                          openRemoveSecondaryTenantModal={
                            this.openRemoveSecondaryTenantModal
                          }
                          openRemoveGuarantorModal={
                            this.openRemoveGuarantorModal
                          }
                        />
                      </VisibleIf>
                    </Collapsible>

                    <Collapsible
                      name="KYC"
                      className="kyc"
                      status={booking.kycsPassed && 'Complete'}>
                      <VisibleIf
                        permission={PERMISSIONS.VIEW_KYC_OVERVIEW}
                        locationRef={locationRef}>
                        <KycTable
                          kycs={kycs}
                          tenants={booking.tenants}
                          guarantor={booking.guarantor}
                          openKycModal={this.openKycModal}
                        />
                      </VisibleIf>
                    </Collapsible>

                    <Collapsible
                      name="Affordability"
                      className="affordability"
                      status={booking.affordabilityPassed && 'Passed'}>
                      <VisibleIf
                        permission={PERMISSIONS.VIEW_AFFORDABILITY_OVERVIEW}
                        locationRef={locationRef}>
                        <AffordabilityTable
                          affordabilities={affordabilities}
                          tenants={booking.tenants}
                          guarantor={booking.guarantor}
                          openAffordabilityModal={this.openAffordabilityModal}
                          affordabilityPassed={booking.affordabilityPassed}
                          openAssessAffordabilityModal={
                            this.openAssessAffordabilityModal
                          }
                        />
                      </VisibleIf>
                    </Collapsible>

                    <Collapsible name="Rent Schedule" className="rentSchedule">
                      <RentScheduleTable
                        rentSchedule={rentSchedule}
                        openCreateRentScheduleModal={
                          this.openCreateRentScheduleModal
                        }
                        canRentScheduleBeGenerated={
                          booking.canRentScheduleBeGenerated
                        }
                      />
                    </Collapsible>

                    <Collapsible
                      name="Agreement"
                      className="agreement"
                      status={booking.agreementSigned && 'Complete'}>
                      <VisibleIf
                        permission={PERMISSIONS.VIEW_AGREEMENT}
                        locationRef={locationRef}>
                        <AgreementsTable
                          agreements={agreements}
                          downloadAgreement={this.downloadAgreement}
                          openAddAgreementModal={this.openAddAgreementModal}
                          openAgreementDetailModal={
                            this.openAgreementDetailModal
                          }
                        />
                      </VisibleIf>
                    </Collapsible>

                    <Collapsible
                      name="Legacy Documents"
                      className="legacyDocuments">
                      <VisibleIf
                        permission={PERMISSIONS.VIEW_LEGACY_DOCUMENTS}
                        locationRef={locationRef}>
                        <LegacyDocumentsTable
                          legacyDocuments={legacyDocuments}
                          openLegacyDocumentUploadModal={
                            this.openLegacyDocumentUploadModal
                          }
                          deleteLegacyDocument={this.deleteLegacyDocument}
                          openLegacyDocumentEditModal={
                            this.openLegacyDocumentEditModal
                          }
                        />
                      </VisibleIf>
                    </Collapsible>
                  </React.Fragment>
                )}
                {this.state.selectedTab == STAY_MANAGEMENT_TAB &&
                  tenancy && (
                    <React.Fragment>
                      <Collapsible
                        name="Room Schedule"
                        className="roomSchedule">
                        <VisibleIf
                          permission={PERMISSIONS.VIEW_ROOM_SCHEDULE}
                          locationRef={locationRef}>
                          <RoomScheduleTable
                            tenancy={tenancy}
                            roomAllocations={roomAllocations}
                            openRoomMoveModal={this.openRoomMoveModal}
                          />
                        </VisibleIf>
                      </Collapsible>

                      <Collapsible
                        name="Early Terminations"
                        className="earlyTermination">
                        <VisibleIf
                          permission={PERMISSIONS.VIEW_EARLY_TERMINATION}
                          locationRef={locationRef}>
                          <EarlyTerminationsTable
                            earlyTerminations={earlyTerminations}
                            openTerminateTenancyModal={
                              this.openTerminateTenancyModal
                            }
                            openDeleteEarlyTerminationModal={
                              this.openDeleteEarlyTerminationModal
                            }
                          />
                        </VisibleIf>
                      </Collapsible>
                    </React.Fragment>
                  )}
                {this.state.selectedTab == FINANCE_TAB &&
                  tenancy && (
                    <React.Fragment>
                      <Collapsible
                        name="Rent Schedule"
                        classNameForTests="ScheduleRent"
                        rightHeader={scheduleBalancesHeader({
                          currentBalance: rentCurrentBalance,
                          totalBalance: _.get(
                            schedules.rent.data,
                            'totals.totalBalance'
                          )
                        })}>
                        <VisibleIf
                          permission={PERMISSIONS.VIEW_RENT_SCHEDULE}
                          locationRef={locationRef}>
                          <Schedule
                            data={schedules.rent.data}
                            scheduleType={SCHEDULE_TYPES.RENT}
                            handleRowClick={this.handleRowClick}>
                            <Schedule.ExtraOptions>
                              <RentScheduleExtraOptions
                                scheduleId={schedules.rent.data.id}
                                scheduleType={SCHEDULE_TYPES.RENT}
                                openCreateCreditNoteModal={
                                  this.openCreateCreditNoteModal
                                }
                                openCreateDraftInvoiceModal={
                                  this.openCreateDraftInvoiceModal
                                }
                                openCreateInvoiceModal={
                                  this.openCreateInvoiceModal
                                }
                                openCreateOverpaymentModal={
                                  this.openCreateOverpaymentModal
                                }
                              />
                            </Schedule.ExtraOptions>
                          </Schedule>
                        </VisibleIf>
                      </Collapsible>
                      <Collapsible
                        name="GoCardless Payment Method"
                        className="gcPaymentMethod">
                        <VisibleIf permission={PERMISSIONS.SETUP_GOCARDLESS}>
                          <GocardlessPaymentMethod
                            invitation={currentGocardlessInvitation}
                            customer={currentGocardlessCustomer}
                            openSetupGocardlessModal={
                              this.openSetupGocardlessModal
                            }
                            openGocardlessCustomerModal={
                              this.openGocardlessCustomerModal
                            }
                            removeInvitation={
                              this.openRemoveGocardlessInvitationModal
                            }
                            removeCustomer={this.removeGocardlessCustomer}
                            openGoCardlessItemUrlLink={
                              this.openGoCardlessItemUrlLink
                            }
                          />
                        </VisibleIf>
                      </Collapsible>
                      <Collapsible
                        name="One-Off Charges Schedule"
                        classNameForTests="ScheduleOneOffCharges"
                        rightHeader={scheduleExtraHeader(
                          oneOffScheduleCurrentBalance
                        )}>
                        <VisibleIf
                          permission={PERMISSIONS.VIEW_ONE_OFF_CHARGES_SCHEDULE}
                          locationRef={locationRef}>
                          <Schedule
                            data={schedules.oneOffCharge.data}
                            scheduleType={SCHEDULE_TYPES.ONE_OFF_CHARGE}
                            handleRowClick={this.handleRowClick}>
                            <Schedule.ExtraOptions>
                              <OneOffChargesScheduleExtraOptions
                                scheduleId={schedules.oneOffCharge.data.id}
                                scheduleType={SCHEDULE_TYPES.ONE_OFF_CHARGE}
                                openCreateCreditNoteModal={
                                  this.openCreateCreditNoteModal
                                }
                                openCreateDraftInvoiceModal={
                                  this.openCreateDraftInvoiceModal
                                }
                                openCreateInvoiceModal={
                                  this.openCreateInvoiceModal
                                }
                                openCreateOverpaymentModal={
                                  this.openCreateOverpaymentModal
                                }
                                openOneOffChargeModal={
                                  this.openOneOffChargeModal
                                }
                              />
                            </Schedule.ExtraOptions>
                          </Schedule>
                        </VisibleIf>
                      </Collapsible>
                      <Collapsible
                        name="Deposit Schedule"
                        classNameForTests="ScheduleDeposit"
                        rightHeader={scheduleExtraHeader(
                          depositCurrentBalance
                        )}>
                        <VisibleIf
                          permission={PERMISSIONS.VIEW_DEPOSIT_SCHEDULE}
                          locationRef={locationRef}>
                          <Schedule
                            data={schedules.deposit.data}
                            scheduleType={SCHEDULE_TYPES.DEPOSIT}
                            handleRowClick={this.handleRowClick}>
                            <Schedule.ExtraOptions>
                              <DepositScheduleExtraOptions
                                scheduleId={schedules.deposit.data.id}
                                scheduleType={SCHEDULE_TYPES.DEPOSIT}
                                openCreateCreditNoteModal={
                                  this.openCreateCreditNoteModal
                                }
                                openCreateDraftInvoiceModal={
                                  this.openCreateDraftInvoiceModal
                                }
                                openCreateInvoiceModal={
                                  this.openCreateInvoiceModal
                                }
                                openCreateOverpaymentModal={
                                  this.openCreateOverpaymentModal
                                }
                              />
                            </Schedule.ExtraOptions>
                          </Schedule>
                        </VisibleIf>
                      </Collapsible>
                      <Collapsible name="Break Clause" className="breakClause">
                        <VisibleIf
                          permission={PERMISSIONS.VIEW_BREAK_CLAUSE}
                          locationRef={locationRef}>
                          <BreakClauseTable
                            breakClause={breakClause}
                            openBreakClauseNoticeRecordModal={
                              this.openBreakClauseNoticeRecordModal
                            }
                          />
                        </VisibleIf>
                      </Collapsible>
                    </React.Fragment>
                  )}
              </DetailsContent.Content>

              <DetailsContent.RightSideBar>
                <BookingDetailRightSideBar
                  bookingId={bookingId}
                  locationRef={locationRef}
                  bookingNotes={bookingNotes}
                />
              </DetailsContent.RightSideBar>
            </DetailsContent>
          )}
        </VisibleIf>

        {cancelBookingModalOpened && <CancelBookingModal />}

        {createOfferModal && <CreateOfferModal />}

        {addAgreementModal && <AddAgreementModal />}

        {sendAgreementModal && <SendAgreementModal />}

        {legacyDocumentUploadModal && <LegacyDocumentUploadModal />}

        {agreementDetailModal && (
          <AgreementDetailModal
            openSendAgreementReminderModal={this.openSendAgreementReminderModal}
            openCancelAgreementModal={this.openCancelAgreementModal}
            openSendAgreementModal={this.openSendAgreementModal}
            openSignAgreementManuallyModal={this.openSignAgreementManuallyModal}
            downloadAgreement={this.downloadAgreement}
          />
        )}
        {sendAgreementReminderModal && <SendAgreementReminderModal />}

        {affordabilityDetailModal && (
          <AffordabilityDetailModal
            openAffordabilityDocumentUploadModal={
              this.openAffordabilityDocumentUploadModal
            }
            openAddEmploymentModal={this.openAddEmploymentModal}
            openConfirmationModal={this.openConfirmationModal}
          />
        )}

        {affordabilityDocumentUploadModal && (
          <AffordabilityDocumentUploadModal />
        )}

        {kycModal && (
          <KycModal
            openKycDocumentUploadModal={this.openKycDocumentUploadModal}
            openConfirmationModal={this.openConfirmationModal}
            openVerifyVisaModal={this.openVerifyVisaModal}
            openAddAddressHistoryModal={this.openAddAddressHistoryModal}
            openRequestKycDocumentModal={this.openRequestKycDocumentModal}
            fetchKycDetails={this.fetchKycDetails}
          />
        )}

        {kycDocumentUploadModal && (
          <KycDocumentUploadModal fetchKycDetails={this.fetchKycDetails} />
        )}

        {addressHistoryModal && (
          <AddressHistoryModal
            getExtraActions={() => [() => this.fetchKycDetails(selectedKycId)]}
          />
        )}
        {kycVerifyVisaModal && (
          <KycVerifyVisaModal fetchKycDetails={this.fetchKycDetails} />
        )}

        {addGuarantorModal && <AddGuarantorModal />}

        {addSecondaryTenantModal && <AddSecondaryTenantModal />}

        {removeSecondaryTenantModal && (
          <RemoveSecondaryTenantModal
            bookingId={bookingId}
            locationRef={locationRef}
            refetchExtraActions={this.refetchExtraActions}
          />
        )}

        {createRentScheduleModal && <CreateRentScheduleModal />}

        {agreementUserInfoModal && <AgreementUserInfoModal />}

        {addEmploymentModal && <AddEmploymentModal />}

        {requestKycDocumentModal && (
          <RequestKycDocumentModal
            openConfirmationModal={this.openConfirmationModal}
            fetchKycDetails={this.fetchKycDetails}
          />
        )}

        {confirmationModal && <ConfirmationModal />}
        {legacyDocumentEditModal && <LegacyDocumentEditModal />}
        {agreementUserTakePhotoModal && <AgreementUserTakePhotoModal />}

        {invoiceDetailModal && (
          <InvoiceDetailModal
            refetchExtraActions={this.financeRefetchExtraActions}
            delayExtraActions={this.financeDelayedExtraActions}
            openConfirmPaymentModal={this.openConfirmPaymentModal}
          />
        )}
        {draftInvoiceDetailModal && (
          <DraftInvoiceDetailModal
            refetchExtraActions={this.financeRefetchExtraActions}
            delayExtraActions={this.financeDelayedExtraActions}
          />
        )}
        {creditNoteDetailModal && (
          <CreditNoteDetailModal
            refetchExtraActions={this.financeRefetchExtraActions}
            delayExtraActions={this.financeDelayedExtraActions}
          />
        )}
        {paymentDetailModal && (
          <PaymentDetailModal
            refetchExtraActions={this.financeRefetchExtraActions}
            delayExtraActions={this.financeDelayedExtraActions}
          />
        )}
        {overpaymentDetailModal && (
          <OverpaymentDetailModal
            refetchExtraActions={this.financeRefetchExtraActions}
            delayExtraActions={this.financeDelayedExtraActions}
          />
        )}
        {refundDetailModal && (
          <RefundDetailModal
            refetchExtraActions={this.financeRefetchExtraActions}
            delayExtraActions={this.financeDelayedExtraActions}
          />
        )}
        {invoiceCreateModal && (
          <InvoiceCreateModal
            refetchExtraActions={this.financeRefetchExtraActions}
            delayExtraActions={this.financeDelayedExtraActions}
          />
        )}
        {draftInvoiceCreateModal && (
          <DraftInvoiceCreateModal
            refetchExtraActions={this.financeRefetchExtraActions}
            delayExtraActions={this.financeDelayedExtraActions}
          />
        )}
        {creditNoteCreateModal && (
          <CreditNoteCreateModal
            refetchExtraActions={this.financeRefetchExtraActions}
            delayExtraActions={this.financeDelayedExtraActions}
          />
        )}
        {overpaymentCreateModal && (
          <OverpaymentCreateModal
            refetchExtraActions={this.financeRefetchExtraActions}
            delayExtraActions={this.financeDelayedExtraActions}
          />
        )}
        {gocardlessCustomerDetailModal && <GocardlessCustomerDetailModal />}
        {gocardlessPaymentDetailModal && (
          <GocardlessPaymentDetailModal refetchExtraActions={fetchSchedule} />
        )}
        {initiateGoCardlessConfirmModal && <InitiateGoCardlessPaymentModal />}
        {cancelGocardlessCustomerModal && <CancelGocardlessCustomerModal />}
        {roomMoveModal && (
          <RoomMoveModal
            locationRef={locationRef}
            tenancy={tenancy}
            bookingId={bookingId}
          />
        )}
        {breakClauseNoticeModal && (
          <BreakClauseNoticeRecordModal
            tenancyId={tenancyId}
            primaryTenant={primaryTenant}
            tenancy={tenancy}
            bookingId={bookingId}
          />
        )}
        {terminateTenancyModal && (
          <TerminateTenancyModal tenancyId={tenancyId} />
        )}
        {confirmTerminationModal && (
          <ConfirmTerminationModal
            tenancyId={tenancyId}
            bookingId={bookingId}
            locationRef={locationRef}
          />
        )}

        {removeGocardlessInvitationModal && (
          <RemoveGocardlessInvitationModal
            tenancyId={tenancyId}
            openRemoveAndVoidGocardlessInvitationModal={
              this.openRemoveAndVoidGocardlessInvitationModal
            }
          />
        )}

        {removeAndVoidGocardlessInvitationModal && (
          <RemoveAndVoidGocardlessInvitationModal tenancyId={tenancyId} />
        )}

        {setupGocardlessModal && (
          <SetupGocardlessModal
            openSendGoCardlessInvitationWarningsModal={
              this.openSendGoCardlessInvitationWarningsModal
            }
          />
        )}

        {sendGocardlessInvitationConfirmModal && (
          <SendGoCardlessInvitationConfirmModal />
        )}
        {oneOffChargeModal &&
          booking && (
            <OneOffChargeModal
              locationRef={locationRef}
              bookingId={bookingId}
              tenancyId={tenancyId}
              openConfirmChargeModal={this.openConfirmChargeModal}
              extraActions={extraActions}
            />
          )}
        {confirmChargeModal &&
          booking && (
            <ConfirmChargeModal
              booking={{
                period: booking.period,
                status: booking.status,
                room: booking.room,
                primaryTenant
              }}
            />
          )}
        {confirmPaymentModal && <ConfirmPaymentModal />}
        {memberCheckOutModal && (
          <MemberCheckOutModal
            getExtraActions={this.memberCheckOutExtraActions}
          />
        )}
      </Page>
    );
  }
}

const mapStateToProps = state => {
  const offers = selectOffers(state);
  const booking = selectBooking(state);
  const {schedules} = state.tenancy.detail;

  const selectHighlightedBalance = x => selectCurrentBalance(state, x);

  const [[primaryTenant], [secondaryTenant]] = _.partition(
    _.get(booking, 'tenants', []),
    tenant => tenant.isPrimary
  );

  return {
    booking,
    modals: {
      oneOffChargeModal: isModalOpen(state, BASE_MODALS.ONE_OFF_CHARGE),
      cancelBookingModalOpened: isModalOpen(
        state,
        BOOKING_MODALS.CANCEL_BOOKING
      ),
      createOfferModal: isModalOpen(state, BOOKING_MODALS.CREATE_OFFER),
      addAgreementModal: isModalOpen(state, BOOKING_MODALS.ADD_AGREEMENT),
      sendAgreementModal: isModalOpen(state, BOOKING_MODALS.SEND_AGREEMENT),
      legacyDocumentUploadModal: isModalOpen(
        state,
        BOOKING_MODALS.LEGACY_DOCUMENT_UPLOAD
      ),
      affordabilityDetailModal: isModalOpen(
        state,
        BOOKING_MODALS.AFFORDABILITY_DETAIL
      ),
      affordabilityDocumentUploadModal: isModalOpen(
        state,
        BOOKING_MODALS.AFFORDABILITY_DOCUMENT_UPLOAD
      ),
      kycModal: isModalOpen(state, BOOKING_MODALS.KYC),
      kycDocumentUploadModal: isModalOpen(
        state,
        BOOKING_MODALS.KYC_DOCUMENT_UPLOAD
      ),
      kycVerifyVisaModal: isModalOpen(state, BOOKING_MODALS.KYC_VERIFY_VISA),
      addressHistoryModal: isModalOpen(state, BOOKING_MODALS.ADDRESS_HISTORY),
      createRentScheduleModal: isModalOpen(
        state,
        BOOKING_MODALS.CREATE_RENT_SCHEDULE
      ),
      addGuarantorModal: isModalOpen(state, BOOKING_MODALS.ADD_GUARANTOR),
      addSecondaryTenantModal: isModalOpen(
        state,
        BOOKING_MODALS.ADD_SECONDARY_TENANT
      ),
      agreementUserInfoModal: isModalOpen(
        state,
        AGREEMENT_USERS_MODALS.AGREEMENT_USER_INFO
      ),
      addEmploymentModal: isModalOpen(state, BOOKING_MODALS.ADD_EMPLOYMENT),
      confirmationModal: isModalOpen(state, BASE_MODALS.CONFIRM),
      agreementDetailModal: isModalOpen(state, BOOKING_MODALS.AGREEMENT_DETAIL),
      signAgreementManuallyModal: isModalOpen(
        state,
        BOOKING_MODALS.SIGN_AGREEMENT_MANUALLY
      ),
      sendAgreementReminderModal: isModalOpen(
        state,
        BOOKING_MODALS.SEND_AGREEMENT_REMINDER
      ),
      requestKycDocumentModal: isModalOpen(
        state,
        BOOKING_MODALS.REQUEST_KYC_DOCUMENT
      ),
      removeSecondaryTenantModal: isModalOpen(
        state,
        BOOKING_MODALS.REMOVE_SECONDARY_TENANT
      ),
      legacyDocumentEditModal: isModalOpen(
        state,
        BOOKING_MODALS.LEGACY_DOCUMENT_EDIT
      ),
      agreementUserTakePhotoModal: isModalOpen(
        state,
        BASE_MODALS.AGREEMENT_USER_TAKE_PHOTO
      ),
      invoiceDetailModal: isModalOpen(state, TENANCY_MODALS.INVOICE_DETAIL),
      draftInvoiceDetailModal: isModalOpen(
        state,
        BOOKING_MODALS.DRAFT_INVOICE_DETAIL
      ),
      invoiceCreateModal: isModalOpen(state, TENANCY_MODALS.INVOICE_CREATE),
      draftInvoiceCreateModal: isModalOpen(
        state,
        BOOKING_MODALS.CREATE_DRAFT_INVOICE
      ),
      creditNoteDetailModal: isModalOpen(
        state,
        TENANCY_MODALS.CREDIT_NOTE_DETAIL
      ),
      creditNoteCreateModal: isModalOpen(
        state,
        TENANCY_MODALS.CREDIT_NOTE_CREATE
      ),
      paymentDetailModal: isModalOpen(state, TENANCY_MODALS.PAYMENT_DETAIL),
      overpaymentCreateModal: isModalOpen(
        state,
        TENANCY_MODALS.OVERPAYMENT_CREATE
      ),
      overpaymentDetailModal: isModalOpen(
        state,
        TENANCY_MODALS.OVERPAYMENT_DETAIL
      ),
      refundDetailModal: isModalOpen(state, TENANCY_MODALS.REFUND_DETAIL),
      gocardlessCustomerDetailModal: isModalOpen(
        state,
        TENANCY_MODALS.GOCARDLESS_CUSTOMER_DETAIL
      ),
      breakClauseNoticeModal: isModalOpen(
        state,
        TENANCY_MODALS.BREAK_CLAUSE_NOTICE_RECORD
      ),
      terminateTenancyModal: isModalOpen(
        state,
        TENANCY_MODALS.TERMINATE_TENANCY
      ),
      gocardlessPaymentDetailModal: isModalOpen(
        state,
        TENANCY_MODALS.GOCARDLESS_PAYMENT_DETAIL
      ),
      confirmTerminationModal: isModalOpen(
        state,
        TENANCY_MODALS.CONFIRM_TERMINATION
      ),
      roomMoveModal: isModalOpen(state, TENANCY_MODALS.ROOM_MOVE),
      initiateGoCardlessConfirmModal: isModalOpen(
        state,
        TENANCY_MODALS.INITIATE_GOCARDLESS_PAYMENT
      ),
      removeGocardlessInvitationModal: isModalOpen(
        state,
        TENANCY_MODALS.REMOVE_GOCARDLESS_INVITATION
      ),
      removeAndVoidGocardlessInvitationModal: isModalOpen(
        state,
        TENANCY_MODALS.REMOVE_AND_VOID_GOCARDLESS_INVITATION
      ),
      setupGocardlessModal: isModalOpen(state, TENANCY_MODALS.SETUP_GOCARDLESS),
      sendGocardlessInvitationConfirmModal: isModalOpen(
        state,
        TENANCY_MODALS.SEND_GOCARDLESS_INVITATION_CONFIRM
      ),
      cancelGocardlessCustomerModal: isModalOpen(
        state,
        TENANCY_MODALS.CANCEL_GOCARDLESS_CUSTOMER
      ),
      confirmChargeModal: isModalOpen(state, BOOKING_MODALS.CONFIRM_CHARGE),
      confirmPaymentModal: isModalOpen(state, TENANCY_MODALS.CONFIRM_PAYMENT),
      memberCheckOutModal: isModalOpen(state, BASE_MODALS.MEMBER_CHECK_OUT)
    },
    offers,
    selectedKycId: selectSelectedKycId(state),
    agreements: selectAgreements(state),
    bookingNotFound: selectBookingNotFound(state),
    kycs: selectKycs(state),
    affordabilities: selectAffordabilities(state),
    legacyDocuments: selectLegacyDocuments(state),
    rentSchedule: selectRentSchedule(state),
    userPermissions: selectCurrentUserPermissions(state),
    tenancy: selectTenancy(state),
    roomAllocations: selectRoomAllocations(state),
    rentCurrentBalance: selectHighlightedBalance(SCHEDULE_TYPES.RENT),
    oneOffScheduleCurrentBalance: selectHighlightedBalance(
      SCHEDULE_TYPES.ONE_OFF_CHARGE
    ),
    depositCurrentBalance: selectHighlightedBalance(SCHEDULE_TYPES.DEPOSIT),
    schedules,
    gocardlessCustomerAndInvitation: selectGocardlessCustomerAndInvitation(
      state
    ),
    earlyTerminations: selectEarlyTerminations(state),
    primaryTenant,
    secondaryTenant,
    bookingNotes: selectBookingNotes(state)
  };
};

const mapDispatchToProps = {
  fetchBooking,
  fetchAgreements,
  fetchRentSchedule,
  fetchKycs,
  fetchAffordabilities,
  fetchLegacyDocuments,
  openModal,
  resetBookingDetailState,
  closeModal,
  passAffordability,
  cancelAgreement,
  signAgreementManually,
  requiresPermission,
  removeSecondaryTenant,
  removeGuarantor,
  fetchKycDetails,
  requiresMultiplePermissions,
  deleteLegacyDocument,
  fetchTenancyRentSchedule,
  fetchDepositSchedule,
  fetchOneOffSchedule,
  fetchRoomAllocations,
  fetchXeroOrganisation,
  fetchEarlyTerminations,
  deleteEarlyTermination,
  fetchGocardlessCustomerAndInvitation,
  removeGocardlessCustomer,
  fetchTenancy,
  delayedAction,
  fetchBookingNotes,
  checkOutTenantsEarly,
  undoCheckInTenants,
  undoCheckOutTenantsEarly
};

export default connect(mapStateToProps, mapDispatchToProps)(
  LoginRequired(BookingDetail)
);
